/* ==========================================================================
   <athena-collection-nav> — the sticky category pill bar.

   Handles smooth-scrolling to a collection section, auto-highlighting the pill
   for the section at the viewport mid-point, and flagging the sticky state so
   the bar can pick up a solid background.

   Extracted from sections/athena-collection.liquid so other sections
   (sections/athena-search-results.liquid) can reuse the same behaviour.
   ========================================================================== */

(function() {
    if (!window.customElements) return;
    if (customElements.get('athena-collection-nav')) return;

    class AthenaCollectionNav extends HTMLElement {
        constructor() {
            super();
            this._scrollHandler = null;
        }

        connectedCallback() {
            if (this.dataset.navMode !== 'custom') {
                this.initializeCollectionNavigation();
            }
            this.initializeStickyDetection();
        }

        disconnectedCallback() {
            // Clean up scroll listener
            if (this._scrollHandler) {
                window.removeEventListener('scroll', this._scrollHandler);
                this._scrollHandler = null;
            }
        }

        initializeCollectionNavigation() {
            // Handle clicks on navigation links
            const links = this.querySelectorAll('.collection-navigation__link[data-link]');
            
            // Check for hash on page load
            const hash = window.location.hash;
            const hashFragment = hash.replace('#', '').split('?')[0];
            const hashSection = document.querySelector(`[data-anchor="${hashFragment}"]`);
            
            if (hashSection) {
              console.log('[Athena Collection Nav] Scrolling to section:', hashSection);
              this.scrollToSection(hashSection);
            }

            // Add click listeners to navigation links
            links.forEach((link) => {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    const targetSection = document.querySelector(`[data-anchor="${link.dataset.link}"]`);
                    
                    if (targetSection) {
                        this.scrollToSection(targetSection);
                    }
                });
            });

            // Set up scroll listener for auto-highlighting
            this._scrollHandler = () => {
                this.handleScrollForSections();
            };
            
            window.addEventListener('scroll', this._scrollHandler);
        }

        scrollToSection(targetSection) {
            const viewportOffset = targetSection.getBoundingClientRect();
            const targetSectionTop = viewportOffset.top;
            
            // Calculate offset based on header height and some padding
            let scrollToY = targetSectionTop - 200 + window.scrollY;

            if (window.innerWidth < 768) {
                scrollToY = targetSectionTop - 150 + window.scrollY;
            }

            window.scrollTo({
                top: scrollToY,
                behavior: "smooth",
            });
        }

        isElementInViewport(el) {
            const rect = el.getBoundingClientRect();
            return (
                rect.top <= (window.innerHeight || document.documentElement.clientHeight) / 2 &&
                rect.bottom >= (window.innerHeight || document.documentElement.clientHeight) / 2
            );
        }

        handleScrollForSections() {
            const sections = document.querySelectorAll('[data-anchor]');
            const links = this.querySelectorAll('.collection-navigation__link[data-link]');
            let activeSection = null;

            for (const section of sections) {
                if (this.isElementInViewport(section)) {
                    activeSection = section;
                    break;
                }
            }

            if (activeSection) {
                const sectionId = activeSection.dataset.anchor;
                const linkEl = this.querySelector(`.collection-navigation__link[data-link="${sectionId}"]`);
                
                if (linkEl) {
                    // Remove current class from all links
                    links.forEach((link) => link.classList.remove('current'));
                    // Add current class to active link
                    linkEl.classList.add('current');
                    
                    // Update active navigation item
                    this.setActiveNavItem(sectionId);
                }
            }
        }

        setActiveNavItem(sectionId) {
            // Remove active from all navigation items and links
            const navItems = this.querySelectorAll('.collection-navigation__item');
            const navLinks = this.querySelectorAll('.collection-navigation__link');
            navItems.forEach(item => {
                item.removeAttribute('data-active');
            });
            navLinks.forEach(link => {
                link.setAttribute('aria-current', 'false');
            });
            
            // Add active to current navigation item and link
            const activeNavItem = this.querySelector(`.collection-navigation__item[data-category="${sectionId}"]`);
            if (activeNavItem) {
                activeNavItem.setAttribute('data-active', 'true');
                const activeLink = activeNavItem.querySelector('.collection-navigation__link');
                if (activeLink) {
                    activeLink.setAttribute('aria-current', 'true');
                }
            }
        }

        initializeStickyDetection() {
            // Use Intersection Observer to detect when element becomes sticky
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Element is not sticky (visible in normal position)
                            this.setAttribute('data-is-sticky', 'false');
                            this.setAttribute('aria-label', 'Collection categories');
                        } else {
                            // Element is sticky (stuck to top)
                            this.setAttribute('data-is-sticky', 'true');
                            this.setAttribute('aria-label', 'Collection categories (sticky navigation)');
                        }
                    });
                },
                {
                    rootMargin: `-${getComputedStyle(this).top} 0px 0px 0px`,
                    threshold: 0
                }
            );

            // Create a sentinel element just before the sticky element
            const sentinel = document.createElement('div');
            sentinel.style.height = '1px';
            sentinel.style.marginBottom = '-1px';
            sentinel.setAttribute('aria-hidden', 'true');
            this.parentNode.insertBefore(sentinel, this);
            
            observer.observe(sentinel);
        }
    }

    customElements.define('athena-collection-nav', AthenaCollectionNav);
})();
